import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Rocket, Gauge, MessageSquarePlus, Plus, Trash2 } from "lucide-react";

const ROADMAP_STATUSES = [
  { key: "not_started", label: "Not started" },
  { key: "in_progress", label: "In progress" },
  { key: "needs_attention", label: "Needs attention" },
  { key: "ready", label: "Ready" },
  { key: "completed", label: "Completed" },
];

export default function AdminFundingControls({ userId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [scoreOverride, setScoreOverride] = useState("");
  const [rangeOverride, setRangeOverride] = useState("");
  const [ready, setReady] = useState(false);
  const [recs, setRecs] = useState([]);
  const [newRec, setNewRec] = useState("");

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/funding/${userId}`);
      setState(data);
      const p = data.profile || {};
      setNotes(p.admin_notes || "");
      setScoreOverride(p.readiness_score_override ?? "");
      setRangeOverride(p.estimated_funding_range_override || "");
      setReady(!!p.funding_ready);
      setRecs(p.custom_recommendations || []);
    } catch (e) {
      // No profile yet — will be created on first save
      setState({ profile: {}, readiness: null });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const save = async () => {
    try {
      await api.put(`/admin/funding/${userId}`, {
        readiness_score_override: scoreOverride === "" ? null : parseInt(scoreOverride),
        estimated_funding_range: rangeOverride || null,
        admin_notes: notes,
        funding_ready: ready,
        custom_recommendations: recs,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRoadmapStep = async (key, status) => {
    try {
      await api.post(`/admin/funding/${userId}/roadmap`, { key, status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addRec = () => {
    if (!newRec.trim()) return;
    setRecs([...recs, newRec.trim()]);
    setNewRec("");
  };
  const removeRec = (i) => setRecs(recs.filter((_, idx) => idx !== i));

  if (loading) return <Card className="p-6 rounded-2xl border-slate-100"><p className="text-sm text-slate-400">Loading funding controls…</p></Card>;

  const readiness = state?.readiness;
  const roadmap = state?.profile?.roadmap || [];

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="admin-funding-controls">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Rocket size={18} /></div>
        <div>
          <h3 className="font-display font-bold text-lg">Funding Controls</h3>
          <p className="text-xs text-slate-500">Override readiness, add custom recommendations, and update the client's roadmap.</p>
        </div>
      </div>

      {readiness && (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 mb-4 text-sm flex flex-wrap gap-3">
          <span className="text-slate-500">Computed score:</span>
          <span className="font-black text-slate-800">{readiness.readiness_score}/100</span>
          <span className="text-slate-500 ml-2">Range:</span>
          <span className="font-black text-slate-800">{readiness.estimated_range}</span>
          <span className="text-slate-500 ml-2">Timeline:</span>
          <span className="font-black text-slate-800">{readiness.timeline}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Readiness score override (0-100)</Label><Input type="number" value={scoreOverride} onChange={(e) => setScoreOverride(e.target.value)} placeholder="leave blank to use computed" data-testid="admin-score-override" /></div>
        <div><Label>Estimated range override</Label><Input value={rangeOverride} onChange={(e) => setRangeOverride(e.target.value)} placeholder="e.g. $50,000–$150,000" data-testid="admin-range-override" /></div>
      </div>
      <label className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-emerald-50 border border-emerald-100 cursor-pointer">
        <Switch checked={ready} onCheckedChange={setReady} data-testid="admin-funding-ready" />
        <span className="text-sm font-semibold text-emerald-800">Mark client as Funding Ready</span>
      </label>

      <div className="mt-4">
        <Label>Admin notes (private)</Label>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-funding-notes" />
      </div>

      <div className="mt-4">
        <Label>Custom recommendations for this client</Label>
        <div className="flex gap-2 mt-1">
          <Input value={newRec} onChange={(e) => setNewRec(e.target.value)} placeholder="Add a specific recommendation…" onKeyDown={(e) => e.key === "Enter" && addRec()} data-testid="admin-new-rec" />
          <Button variant="outline" onClick={addRec}><Plus size={16} /></Button>
        </div>
        <div className="mt-2 space-y-2">
          {recs.map((r, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100 text-sm" data-testid={`admin-rec-${i}`}>
              <span className="flex-1 text-slate-800">{r}</span>
              <Button size="icon" variant="ghost" onClick={() => removeRec(i)} className="text-red-600"><Trash2 size={14} /></Button>
            </div>
          ))}
          {recs.length === 0 && <p className="text-xs text-slate-400">No custom recommendations yet.</p>}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={save} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-funding-save">Save funding controls</Button>
      </div>

      {roadmap.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Gauge size={16} className="text-slate-500" />
            <h4 className="font-display font-bold text-base">Funding Roadmap</h4>
          </div>
          <div className="space-y-2">
            {roadmap.map((step, idx) => (
              <div key={step.key} className="flex items-center gap-2 flex-wrap p-2 rounded-xl bg-slate-50 border border-slate-100" data-testid={`admin-roadmap-${step.key}`}>
                <span className="text-xs font-bold text-slate-500 min-w-[16px]">{idx + 1}.</span>
                <span className="flex-1 text-sm font-semibold text-slate-800">{step.label}</span>
                <div className="flex flex-wrap gap-1">
                  {ROADMAP_STATUSES.map((s) => (
                    <button key={s.key} onClick={() => setRoadmapStep(step.key, s.key)} data-testid={`admin-roadmap-set-${step.key}-${s.key}`} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${step.status === s.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
